import { useTranslation } from 'react-i18next';
import { Chunk, Job, MAX_ATTEMPTS } from '@quizdb/common';

/** The chunks that are no longer running or queued: done or failed (QZ-D56). */
const finishedChunkCount = (job: Job): number =>
  job.chunks.filter((chunk) => chunk.status === 'done' || chunk.status === 'failed').length;

/** A job's status as a tinted pill, with an optional bar while it runs (QZ-D56, QZ-D57). */
export const JobStatusPill = ({
  job,
  showProgress = false,
}: {
  job: Job;
  showProgress?: boolean;
}) => {
  const { t } = useTranslation();
  const hasFailedChunk = job.chunks.some((chunk) => chunk.status === 'failed');

  let modifier = '';
  let text = t(`job.status.${job.status}`);
  if (job.status === 'planning' || job.status === 'running') {
    modifier = ' is-running';
  } else if (job.status === 'done' && hasFailedChunk) {
    modifier = ' is-warning';
    text = t('job.status.doneWithFailures');
  } else if (job.status === 'done') {
    modifier = ' is-done';
  } else if (job.status === 'failed') {
    modifier = ' is-failed';
  }

  const showBar = showProgress && job.status === 'running' && job.chunks.length > 0;
  const percent = showBar ? Math.round((100 * finishedChunkCount(job)) / job.chunks.length) : 0;

  return (
    <>
      <span className={`qz-status${modifier}`}>{text}</span>
      {showBar && (
        <div className="progress mt-2" role="progressbar" aria-valuenow={percent}>
          <div className="progress-bar" style={{ width: `${percent}%` }} />
        </div>
      )}
    </>
  );
};

/** A chunk's status as a tinted pill, with the retry count after its first attempt (QZ-D56). */
export const ChunkStatusPill = ({ chunk }: { chunk: Chunk }) => {
  const { t } = useTranslation();

  if (chunk.status === 'running' && chunk.attempts > 1) {
    return (
      <span className="qz-status is-warning">
        {t('job.chunkStatus.retrying', { attempt: chunk.attempts, max: MAX_ATTEMPTS })}
      </span>
    );
  }

  const modifiers: Record<Chunk['status'], string> = {
    queued: '',
    running: ' is-running',
    done: ' is-done',
    failed: ' is-failed',
  };

  return (
    <span className={`qz-status${modifiers[chunk.status]}`}>
      {t(`job.chunkStatus.${chunk.status}`)}
    </span>
  );
};
